import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useDeleteTRSRegistration from './queries/useDeleteTRSRegistration';
import useGetTRSRegistration from './queries/useGetTRSRegistration';
import useUpdateTRSRegistration from './queries/useUpdateTRSRegistration';

import FormBase from '@/components/forms/new/FormBase';
import { Button, LoadingIndicator } from 'components/v4';
import PasswordField from '../../resources/pbx/formFields/PasswordField';
import UsernameField from '../../resources/pbx/formFields/UsernameField';
import AdvancedTransferField from './formFields/AdvancedTransferField';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AuthenticationField from './formFields/AuthenticationField';
import ExtensionField from './formFields/ExtensionField';
import TRSRegistrationActiveField from './formFields/TRSRegistrationActiveField';
import TRSRegistrationRegistrarField from './formFields/TRSRegistrationRegistrarField';

import { aorsPath } from './constants';

const header = 'TRS Registration Details';

const TRSRegistrationEdit = () => {
    const navigate = useNavigate();
    const { id: TRSRegistrationID } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const { restError } = useSelector((state) => state.modal);

    const [isDisabled, setIsDisabled] = useState(true);
    const [currentRegistration, setCurrentRegistration] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Fetch single registration data
    const {
        data: registrationData,
        isLoading: registrationLoading,
        error: registrationError,
        isFetching: registrationFetching,
    } = useGetTRSRegistration(TRSRegistrationID);

    /**@example '/aors/{registrationId}?registrar={registrarId}' */
    const aorsPathWithRegistrar = `${aorsPath}${location.search}`;
    const breadCrumbTrail = [
        { label: 'Resources', path: `${location.search}` },
        {
            label: 'TRS Registration Management',
            path: aorsPathWithRegistrar,
        },
        { label: 'Edit Registration', path: `${location.search}` },
    ];

    useEffect(() => {
        if (registrationData) {
            setCurrentRegistration(registrationData);
        }
    }, [registrationData]);

    const handleUpdateSuccess = () => {
        toast.success('TRS Registration updated successfully!');
        navigate(aorsPathWithRegistrar);
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update TRS Registration:', error);
        toast.error(
            restError ?? 'Failed to update TRS Registration. Please try again.',
        );
    };

    const handleDeleteSuccess = () => {
        toast.success('TRS Registration deleted successfully!');
        navigate(aorsPathWithRegistrar);
        onClose();
    };

    const handleDeleteError = (error) => {
        console.error('Failed to delete TRS Registration:', error);
        toast.error(
            restError ?? 'Failed to delete TRS Registration. Please try again.',
        );
    };

    const { mutate: updateRegistration, isPending: updatePending } =
        useUpdateTRSRegistration(handleUpdateSuccess, handleUpdateError);

    const { mutate: deleteRegistration, isPending: deletePending } =
        useDeleteTRSRegistration(handleDeleteSuccess, handleDeleteError);

    const finalFormSubmit = async (values) => {
        // Validate credentials if override is disabled
        if (
            values.overrideCredentials &&
            (!values.username || !values.password)
        ) {
            const errorMessage =
                'Username and password are required when not using registrar authentication';
            console.error(errorMessage);
        }

        let _changePassword = false;

        if (values.overrideCredentials && values.password) {
            _changePassword = true;
        } else {
            _changePassword = false; // user left the password field empty
        }

        const finalSubmit = {
            id: TRSRegistrationID,
            overrideCredentials: values.overrideCredentials, // true means username & password override registrar, false means username and password null
            username: values.overrideCredentials ? values.username : '',
            changePassword: _changePassword,
            password: values.overrideCredentials ? values.password : '',
            isEnabled: values.isEnabled,
            useAdvancedTransfer: values.useAdvancedTransfer || false,
        };

        updateRegistration(finalSubmit);
    };

    const handleDeleteConfirm = () => {
        deleteRegistration(TRSRegistrationID);
    };

    const formFields = (errors, touched, values) => {
        // Check if credentials are required when not using override
        const hasCredentials =
            !values?.overrideCredentials ||
            (values?.username && values?.password);

        const hasRequiredFields = hasCredentials;

        // Update form submission state
        if (hasRequiredFields) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        const fields = [
            <TRSRegistrationActiveField
                key="isEnabled"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={updatePending}
            />,
            <TRSRegistrationRegistrarField
                key="registrarID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                registrationData={currentRegistration}
                isDisabled // Always read-only
            />,
            <ExtensionField
                key="extension"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled // Always read-only
                mode="Edit"
            />,
            <AdvancedTransferField
                key="useAdvancedTransfer"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={updatePending}
            />,
            // Add padding when this is the last field (no custom credentials)
            <Box
                key="overrideCredentials"
                mt="0!important"
                pb={values?.overrideCredentials ? 0 : '12px'}>
                <AuthenticationField
                    errors={errors}
                    touched={touched}
                    values={values}
                    darkMode={darkMode}
                    isDisabled={updatePending}
                />
            </Box>,
        ];

        // Show Username/Password fields when authentication override is enabled
        if (values?.overrideCredentials) {
            fields.push(
                <UsernameField
                    key="username"
                    errors={errors}
                    touched={touched}
                    darkMode={darkMode}
                    isDisabled={updatePending}
                />,
            );

            // Add padding to last field
            fields.push(
                <Box key="password" mt="0!important" pb="12px">
                    <PasswordField
                        errors={errors}
                        touched={touched}
                        darkMode={darkMode}
                        isDisabled={updatePending}
                    />
                </Box>,
            );
        }

        return fields;
    };

    const extraButton = (
        <Flex justify="flex-end" width="100%">
            <Button
                w={142}
                colorScheme="red"
                onClick={onOpen}
                isDisabled={deletePending || updatePending}>
                Delete
            </Button>
        </Flex>
    );

    const editInitialValues = useMemo(() => {
        if (!currentRegistration) return {};

        return {
            isEnabled: currentRegistration.isEnabled || false,
            registrarID: currentRegistration.registrarID || '',
            extension: currentRegistration.extension || '',
            overrideCredentials:
                Boolean(
                    currentRegistration.username &&
                        currentRegistration.password,
                ) || false, // Infer from username/password presence
            username: currentRegistration.username || '',
            password: currentRegistration.password || '',
            useAdvancedTransfer:
                currentRegistration.useAdvancedTransfer || false,
        };
    }, [currentRegistration]);

    const isFetchingData =
        registrationLoading || registrationFetching || updatePending;

    // Handle error states
    if (registrationError) {
        toast.error(
            'Failed to load registration data. Redirecting to registrations list.',
        );
        navigate(aorsPath);
        return <LoadingIndicator />;
    }

    // Show loading while data is being fetched
    if (isFetchingData || !currentRegistration) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <FormBase
                isDisabled={isDisabled}
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={editInitialValues}
                finalFormSubmit={finalFormSubmit}
                backLink={[
                    `${aorsPath}${location.search}`,
                    { state: { index: 0 } },
                ]}
                extraButton={extraButton}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                registrationExtension={currentRegistration?.extension}
                onConfirm={handleDeleteConfirm}
                isDeleting={deletePending}
            />
        </>
    );
};

export default TRSRegistrationEdit;
